/* eslint-disable react/forbid-prop-types */
import React from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import { getSrc } from 'gatsby-plugin-image';

const query = graphql`
  query StructuredData {
    site {
      siteMetadata {
        url
        defaultTitle: title
        defaultDescription: description
      }
    }
    defaultImage: file(relativePath: { eq: "default.png" }) {
      childImageSharp {
        gatsbyImageData(layout: FIXED, width: 1440, height: 900, quality: 100)
      }
    }
  }
`;

const StructuredData = ({
  type,
  title,
  description,
  pathname,
  image,
  datePublished,
  dateModified,
  author,
  startDate,
  endDate,
  location,
}) => {
  const {
    site: {
      siteMetadata: { url, defaultTitle, defaultDescription },
    },
    defaultImage,
  } = useStaticQuery(query);

  // Determine the image URL
  let imageUrl = null;
  if (image?.localFile?.publicURL) {
    const parts = image.localFile.publicURL.split('/');
    const filename = parts.pop();
    const encodedFilename = encodeURI(filename);
    const encodedPath = [...parts, encodedFilename].join('/');
    imageUrl = new URL(encodedPath, url).toString();
  } else if (defaultImage) {
    const defaultSrc = getSrc(defaultImage);
    if (defaultSrc) {
      imageUrl = new URL(defaultSrc, url).toString();
    }
  }

  // Only render structured data if we have the minimum required fields
  if (!type || !title || !pathname) {
    return null;
  }

  const fullUrl = new URL(pathname, url).toString();

  // Base structured data object
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': type,
    headline: title,
    description: description || defaultDescription,
    url: fullUrl,
  };

  // Add image if available (required for Article types)
  if (imageUrl) {
    structuredData.image = imageUrl;
  }

  // Add dates if available
  if (datePublished) {
    structuredData.datePublished = datePublished;
  }

  if (dateModified) {
    structuredData.dateModified = dateModified;
  } else if (datePublished) {
    // Fallback to datePublished if dateModified is not available
    structuredData.dateModified = datePublished;
  }

  // Add author information
  if (author) {
    structuredData.author = {
      '@type': 'Organization',
      name: author,
    };
  }

  // Add Event-specific fields
  if (type === 'Event') {
    if (startDate) {
      structuredData.startDate = startDate;
    }
    if (endDate) {
      structuredData.endDate = endDate;
    }
    if (location) {
      structuredData.location = {
        '@type': 'Place',
        name: location,
      };
    }
    // Add organizer for events
    structuredData.organizer = {
      '@type': 'Organization',
      name: defaultTitle,
      url: url,
    };
  }

  // Add publisher information (required for Article types)
  if (type === 'Article' || type === 'BlogPosting' || type === 'NewsArticle') {
    structuredData.publisher = {
      '@type': 'Organization',
      name: defaultTitle,
      logo: {
        '@type': 'ImageObject',
        url: new URL('/static/portable-logo.png', url).toString(),
      },
    };

    // Ensure mainEntityOfPage is set
    structuredData.mainEntityOfPage = {
      '@type': 'WebPage',
      '@id': fullUrl,
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

StructuredData.propTypes = {
  type: PropTypes.oneOf(['Article', 'BlogPosting', 'NewsArticle', 'WebPage', 'Event']),
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  pathname: PropTypes.string.isRequired,
  image: PropTypes.object,
  datePublished: PropTypes.string,
  dateModified: PropTypes.string,
  author: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  location: PropTypes.string,
};

StructuredData.defaultProps = {
  type: 'Article',
  description: null,
  image: null,
  datePublished: null,
  dateModified: null,
  author: 'Portable',
  startDate: null,
  endDate: null,
  location: null,
};

export default StructuredData;
